<template lang="pug">
EcreativAsyncVideoPlayer.w-100.h-100(
  :key="video.videoId"
  :videoId="video.videoId"
  :title="video.title"
  :subTitle="video.subTitle"
  :featured="video.featured"
  :loading="playerStates[video.videoId]?.loading"
  :error="playerStates[video.videoId]?.error"
  :isRounded="true"
  @update-last-player="(videoRef) => playerOnClick(videoRef)"
  @init-player="(videoRef) => initPlayer(video.videoId, videoRef)"
)
  template(#metadata='')
    EcreativArchetypesHeading.mb-1.pt-3(
      v-if="video.title",
      tag="h4",
      size="6",
      context="white"
    ) {{ video.title }}
    EcreativArchePrimitivesParagraph.mb-1.pt-3(
      v-if="video.subTitle",
      tag="h4",
      context="white",
      size="6"
    ) {{ video.subTitle }}
    time.text-white(
      v-if="video.time",
      :datetime="video.time"
    ) {{ video.time }}
  template(#overlay='')
    EcreativArchetypesHeading.mb-auto(
      v-if="video.title",
      tag="h4",
      size="6",
      context="white"
    ) {{ video.title }}
  template(#button='')
    .playButton(role="button" aria-label="Play video")
      i.playButton__icon
        EcreativAtomsSvgIcon(
          iconName="playFill",
          iconSize="sm",
          isCustom
        )
</template>

<script setup lang="ts">
import { nextTick } from 'vue';
import { useYouTubePlayerStore } from '@/stores/youtubePlayerStore';

export interface Video {
  videoId: string;
  title: string;
  subTitle?: string;
  time?: string;
  featured?: boolean;
}

const props = defineProps<{
  video: Video;
  timestamp?: number;
}>();

const store = useYouTubePlayerStore();
const playerStates = store.playerStates;

const playerOnClick = async (videoRef: { value: HTMLElement | null }) => {
  if (!videoRef?.value) return;

  const videoId = videoRef.value.dataset.embed;
  if (!videoId) return;
  await store.createAndPlay(videoId, videoRef.value, props.timestamp || 0);
};

const initPlayer = async (
  videoId: string,
  videoRef: { value: HTMLElement | null },
) => {
  try {
    await nextTick(); // Only tick, not ensureYoutubeApiReady again
    store.registerPlayer(videoId, videoRef.value!);
    store.setBestThumbnail(videoId);
    store.setPlayerLoading(videoId, false, null);
  } catch (err) {
    console.error(`initPlayer failed for ${videoId}`, err);
    store.setPlayerLoading(videoId, false, 'Failed to initialize player');
  }
};
</script>

<style lang="scss" scoped>
$block: '.playButton';

#{$block} {
  #{$block}__icon {
    position: absolute;
    right: 30px;
    bottom: 15px;
    z-index: 10;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 8px 0;
    background-color: rgb(255 255 255);
    color: $primary;
    box-shadow:
      0 2px 2px 0 rgb(55 57 58 / 14%),
      0 3px 1px -2px rgb(52 68 70 / 20%),
      0 1px 5px 0 rgb(25 27 32 / 12%);
    font-size: 1.4rem;
    transition: all 0.2s ease-in;
    border-radius: 74px;
  }

  &:hover {
    cursor: pointer;

    #{$block}__icon {
      background-color: rgb(255 255 255);
      color: $secondary;
      box-shadow:
        0 2px 2px 0 rgb(0 188 212 / 14%),
        0 3px 1px -2px rgb(0 188 212 / 20%),
        0 1px 5px 0 rgb(0 188 212 / 12%);
      fill: $primary;
      fill-opacity: 1;
    }
  }
}
</style>
